import {ReactElement, useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import styled, {css, keyframes} from "styled-components";

import HamburgerButton from "@/assets/hamburgerButton.svg?react";
import IconCross from "@/assets/iconCross.svg?react";
import Logo from "@/assets/logo.svg?react";
import {Navigation} from "@/components/Layout/Navigation/Navigation";
import {AppContext} from "@/context/AppContext";
import {LogContext, LogContextType} from "@/context/LogContex";
import {quicksandMedium, quicksandSemiBold} from "@/GlobalStyle";
import {useGetContext} from "@/hooks/useGetContext/useGetContext";
import {AppContext as AppContextType} from "@/ts/appContext";

interface StyleHeaderContentType {
  $initialNavWidth: boolean;
  $isCollapsed: boolean;
}
interface StyleHeaderType {
  $navIsOpen: boolean;
  $isCollapsed: boolean;
}
const depliementNav = keyframes`
  0% {
    overflow: hidden;
    max-height: 70px;
  }

  100% {
    max-height: 500px;
    overflow: visible;
  }`;

const StyledLogoContainer = styled.div`
  height: 70px;
  display: flex;
  align-items: center;
`;
const StyledLogo = styled(Logo)`
  min-width: 2.75rem;
  width: 2.75rem;
  margin: 0 20px;
  &:hover {
    cursor: pointer;
  }
`;
const StyledHeaderContent = styled.div<StyleHeaderContentType>`
  width: 100%;
  height: ${({$isCollapsed}) => ($isCollapsed ? "auto" : "70px")};
  opacity: ${({$initialNavWidth}) => ($initialNavWidth ? 1 : 0)};

  display: flex;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-items: center;
`;
const StyledHeader = styled.header<StyleHeaderType>`
  z-index: 2;
  user-select: none;
  position: absolute;
  top: 0;
  width: 100%;
  height: fit-content;
  background-color: white;
  overflow: hidden;
  border-bottom: ${({theme}) => theme.colors.darkPrimary} 2px solid;
  box-shadow: 0 5px 6px #00000040;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  ${({$isCollapsed, $navIsOpen}) =>
    $isCollapsed && $navIsOpen
      ? css`
          animation: ${depliementNav} 0.5s ease-in-out forwards;
        `
      : css`
          max-height: 70px;
        `}
`;
const StyledContentNav = styled.nav`
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const StyledHamburgerButton = styled(HamburgerButton)`
  fill: ${({theme}) => theme.colors.mediumSecondary};
  width: 40px;
  padding: 0 20px;
`;
const StyledIconCross = styled(IconCross)`
  fill: ${({theme}) => theme.colors.mediumSecondary};
  width: 40px;
  padding: 0 20px;
`;
const SloganContainer = styled.div<{$isCollapsed: boolean}>`
  container-type: inline-size;
  width: 100%;
  height: 70px;
`;
const StyledSlogan = styled.div`
  flex: 1;
  height: 100%;
  transform: skew(-20deg);
  background: ${({theme}) => theme.colors.darkPrimary};
  display: flex;
  align-items: center;
  justify-content: center;
  p {
    color: white;
    ${quicksandMedium}
    transform: skew(20deg);
    padding: 0 10px;
    font-size: 1rem;
    text-align: center;
  }

  @container (max-width: 220px) {
    & p {
      display: none;
    }
  }
  @container (max-width: 3px) {
    display: none;
  }
`;
const StyledLogButton = styled.div<{$isCollapsed: boolean}>`
  width: 140px;
  height: 70px;
  transform: skew(-20deg);
  padding: 0 30px 0 0;
  margin: 0 -17px 0 0;
  border-left: ${({$isCollapsed, theme}) =>
    $isCollapsed ? `4px solid ${theme.colors.darkPrimary}` : "none"};
  p {
    margin: 0 0 0 20px;
    line-height: 70px;
    transform: skew(20deg);
    color: ${({theme}) => theme.colors.mediumSecondary};
    ${quicksandSemiBold}
  }
  &:hover {
    cursor: pointer;
    background-color: ${({theme}) => theme.colors.mediumSecondary};
    p {
      color: white;
    }
  }
`;

const StyledResponsiveLogButton = styled.div`
  width: 100%;
  height: 60px;
  border-top: 2px solid ${({theme}) => theme.colors.mediumSecondary};
  p {
    margin: 0 0 0 0px;
    line-height: 60px;
    text-align: center;
    color: ${({theme}) => theme.colors.mediumSecondary};
    ${quicksandSemiBold}
  }
  &:hover {
    cursor: pointer;
    background-color: ${({theme}) => theme.colors.mediumSecondary};
    p {
      color: white;
    }
  }
`;

export function Header(): ReactElement {
  const {handleLogout, isConnected} = useGetContext<LogContextType>(
    LogContext,
    "LogContext"
  );
  const navigate = useNavigate();
  const {userType} = useGetContext<AppContextType>(AppContext, "AppContext");
  const {droits} = useGetContext<LogContextType>(LogContext, "LogContext");
  const navRef = useRef<HTMLUListElement>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [initialNavWidth, setInitialNavWidth] = useState<number | null>(null);
  const [navIsOpen, setNavIsOpen] = useState<boolean>(false);
  const updateCollapseState = (): void => {
    if (navRef.current) {
      const currentNavWidth = navRef.current.scrollWidth;
      const viewportWidth = window.innerWidth;

      if (initialNavWidth === null) {
        if (currentNavWidth) {
          // Enregistrer la largeur réelle au premier rendu
          setInitialNavWidth(currentNavWidth);
          setIsCollapsed(currentNavWidth > viewportWidth - 250);
        }
      } else {
        // Comparer par rapport à la largeur enregistrée
        setIsCollapsed(initialNavWidth > viewportWidth - 250);
        setNavIsOpen(false);
      }
    }
  };

  useEffect(() => {
    updateCollapseState(); // au chargement
    window.addEventListener("resize", updateCollapseState);
    return () => window.removeEventListener("resize", updateCollapseState);
  }, [initialNavWidth]);
  // navigation pour les utilisateurs étant des adhérents

  // Créer un objet de mapping pour simplifier la logique
  const navigationMap = {
    Adherent: [
      {libelle: "Mes contrats", url: "/"},
      {libelle: "Fournisseur", url: "/fournisseurs"},
    ],
    Collaborateur: [
      // {libelle: "Adhérent", url: "/"},
      {libelle: "Fournisseur", url: "/fournisseurs"},
      {
        libelle: "Gestion KPI",
        url: "/historique_kpi",
        droit: droits?.["coact.campagne.lister"],
      },
    ],
  };

  const navigationURL = isConnected
    ? navigationMap[userType as keyof typeof navigationMap] || []
    : [];
  const nav = (
    <Navigation
      navigation={navigationURL}
      isCollapsed={isCollapsed}
      navRef={navRef}
      initialNavWidth={initialNavWidth}
      isOpen={navIsOpen}
      setNavIsOpen={setNavIsOpen}
    />
  );

  return (
    <StyledHeader $isCollapsed={isCollapsed} $navIsOpen={navIsOpen}>
      <StyledHeaderContent
        data-testid="StyledHeaderContent"
        $initialNavWidth={initialNavWidth === null ? false : true}
        $isCollapsed={isCollapsed}>
        <StyledLogoContainer data-testid="StyledLogoContainer">
          <StyledLogo data-testid="StyledLogo" onClick={() => navigate("/")} />
        </StyledLogoContainer>
        <StyledContentNav data-testid="StyledContentNav">
          {!isCollapsed ? nav : null}
        </StyledContentNav>
        <SloganContainer
          $isCollapsed={isCollapsed}
          data-testid="SloganContainer">
          <StyledSlogan data-testid="StyledSlogan">
            <p>Des actions mesurées pour des résultats partagés.</p>
          </StyledSlogan>
        </SloganContainer>
        {isCollapsed ? (
          navIsOpen ? (
            <StyledIconCross
              data-testid="StyledIconCross"
              onClick={() => {
                setNavIsOpen(prev => !prev);
              }}
            />
          ) : (
            <StyledHamburgerButton
              data-testid="StyledHamburgerButton"
              onClick={() => {
                setNavIsOpen(prev => !prev);
              }}
            />
          )
        ) : null}
        {!isCollapsed && isConnected ? (
          <StyledLogButton
            $isCollapsed={isCollapsed}
            data-testid="StyledLogButton"
            onClick={handleLogout}>
            <p>Deconnexion</p>
          </StyledLogButton>
        ) : null}
      </StyledHeaderContent>
      {isCollapsed ? nav : null}
      {isCollapsed && navIsOpen && isConnected ? (
        <StyledResponsiveLogButton>
          <p>Deconnexion</p>
        </StyledResponsiveLogButton>
      ) : null}
    </StyledHeader>
  );
}
